/**
 * Acceso tipado a la bandeja de salida (outbox) de la sincronización offline.
 * La tabla `outbox` vive en Dexie (migración version(13) en `db.ts`).
 */

import { localDb } from "../data/adapters/dexie/db";
import type { OutboxEntry, OutboxEstado } from "./outboxTypes";

export type { OutboxEntry, OutboxEstado, OutboxOp } from "./outboxTypes";

/** Encola una operación de escritura pendiente. Devuelve el id de la entrada. */
export async function encolar(
  e: Omit<OutboxEntry, "id" | "estado" | "intentos" | "createdAt">,
): Promise<string> {
  const entry: OutboxEntry = {
    ...e,
    id: crypto.randomUUID(),
    estado: "pendiente",
    intentos: 0,
    createdAt: new Date().toISOString(),
  };
  await localDb.outbox.add(entry);
  return entry.id;
}

/**
 * Reactiva entradas que quedaron en 'enviando' (un drenado anterior murió a
 * mitad). Se llama al inicio de cada sincronización para no perderlas.
 */
export async function reactivarColgadas(tenantId: string): Promise<void> {
  const rows = await localDb.outbox.where("tenantId").equals(tenantId).toArray();
  for (const r of rows) {
    if (r.estado === "enviando") await localDb.outbox.update(r.id, { estado: "pendiente" });
  }
}

/** Entradas a sincronizar (pendiente/error) del tenant, en orden FIFO. */
export async function pendientes(tenantId: string): Promise<OutboxEntry[]> {
  const rows = await localDb.outbox.where("tenantId").equals(tenantId).toArray();
  return rows
    .filter((r) => r.estado === "pendiente" || r.estado === "error")
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** Cuenta de entradas por sincronizar (pendiente/error). */
export async function contarPendientes(tenantId: string): Promise<number> {
  return (await pendientes(tenantId)).length;
}

/**
 * Cuenta TODAS las entradas sin sincronizar (pendiente/error/conflicto/enviando):
 * cualquier fila en el outbox es trabajo no confirmado en el servidor. Lo usa el
 * aviso de cierre de sesión, porque la purga las borraría.
 */
export async function contarSinSincronizar(tenantId: string): Promise<number> {
  return localDb.outbox.where("tenantId").equals(tenantId).count();
}

/** Entradas marcadas como conflicto (requieren resolución manual). */
export async function enConflicto(tenantId: string): Promise<OutboxEntry[]> {
  const rows = await localDb.outbox.where("tenantId").equals(tenantId).toArray();
  return rows.filter((r) => r.estado === "conflicto");
}

/** Cambia el estado (y el error opcional) de una entrada. */
export async function marcar(
  id: string,
  estado: OutboxEstado,
  error?: string,
): Promise<void> {
  await localDb.outbox.update(id, { estado, ...(error !== undefined ? { error } : {}) });
}

/** Incrementa el contador de intentos y deja la entrada en `pendiente`. */
export async function incrementarIntento(id: string): Promise<void> {
  const e = await localDb.outbox.get(id);
  if (e) await localDb.outbox.update(id, { intentos: e.intentos + 1, estado: "pendiente" });
}

/** Elimina una entrada (tras sincronizar con éxito o descartar). */
export async function eliminar(id: string): Promise<void> {
  await localDb.outbox.delete(id);
}

/**
 * Remapea el id de una entidad en las entradas PENDIENTES del outbox
 * (args[0]): tras reconciliar un create/upsertDia, las operaciones encoladas
 * que referenciaban el id temporal pasan a referenciar el id real del servidor.
 */
export async function remapearArgId(
  tenantId: string,
  entity: string,
  viejoId: string,
  nuevoId: string,
): Promise<void> {
  if (!nuevoId || viejoId === nuevoId) return;
  const rows = await localDb.outbox.where("tenantId").equals(tenantId).toArray();
  for (const r of rows) {
    if (
      r.entity === entity &&
      r.estado !== "enviando" &&
      Array.isArray(r.args) &&
      r.args[0] === viejoId
    ) {
      const nuevos = [...r.args];
      nuevos[0] = nuevoId;
      await localDb.outbox.update(r.id, { args: nuevos });
    }
  }
}
